import { useLazyQuery, useQuery } from "@apollo/client"
import { useNavigate, useParams } from "react-router-dom"
import { GET_PATIENT_CASE } from "../../graphql/queries"
import { IGetPatientCaseOutput, IGetPatientCaseInput } from "../../graphql/types/queries/patientCases"
import { Box, Card, Flex, Text } from "rebass/styled-components"
import styled from "styled-components"
import { FilledFormViewer } from "./components/FilledFormViewer"
import { useEffect, useState } from "react"
import Modal from "styled-react-modal"
import { SpecialModalBackground } from "../../components/Commons"
import PleaseWait from "../../components/PleaseWait"

const Description = styled(Text)`
  font-size: 12px;
  opacity: 0.7;
  font-weight: 300;
`

const Value = styled(Text)`
  margin-left: 16px;
  margin-bottom: 16px;
  &.noneVal {
    font-weight: 400;
    font-style: italic;
    opacity: 0.5;
    font-size: 12px;
  }
`

const Wrapper = styled(Flex)`
  overflow: auto;
  width: 800px;
  padding-right: 20px;
  margin-right: 20px;
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #6c89b7;
    border-radius: 5px;
  }
  &::-webkit-scrollbar {
    width: 7px;
    border: 1px solid transparent;
  }
`;

const Back = styled(Text)`
  text-decoration: underline;
  margin-bottom: 20px;
  cursor: pointer;

  @media screen and (min-width: 68.8125em) {
    display: none;
  }
`

const Content: React.FC<{
  data: IGetPatientCaseOutput | undefined
}> = ({ data }) => {
  if (!!data?.providerGetPatientCase?.filledForm) {
    return (
      <FilledFormViewer values={data?.providerGetPatientCase?.filledForm.values}
        formName={data?.providerGetPatientCase?.filledForm.form?.name || data?.providerGetPatientCase?.subject || ''}
        updatedAt={data?.providerGetPatientCase?.filledForm.updatedAt}
        updatedBy={data?.providerGetPatientCase?.filledForm.healthCoach?.displayName || 'Health Coach'}
        formDescription={data?.providerGetPatientCase?.filledForm.form?.description} />
    )
  }

  return (
    <Flex flexDirection={'column'}>
      <Text fontSize={'18px'} mt={3}>{`patient case - ${data?.providerGetPatientCase?.subject} #${data?.providerGetPatientCase?.patientCaseId}`}</Text>
      <Box style={{ background: '#777', height: '1px', width: '100%', opacity: 0.3 }} mt={2} mb={3}></Box>
      <Description>Source/Recipient</Description>
      <Value className={!data?.providerGetPatientCase?.documentSource ? 'noneVal' : ''}>{data?.providerGetPatientCase?.documentSource || 'none'}</Value>
      <Description>Type</Description>
      <Value className={!data?.providerGetPatientCase?.documentSubclass ? 'noneVal' : ''}>{data?.providerGetPatientCase?.documentSubclass ? (data?.providerGetPatientCase?.documentSubclass?.replace(`${data.providerGetPatientCase.documentClass}_`, '') || '').toLowerCase() : 'none'}</Value>
      <Description>Provider</Description>
      <Value className={!data?.providerGetPatientCase?.providerUsername ? 'noneVal' : ''}>{data?.providerGetPatientCase?.providerUsername || 'none'}</Value>
      <Description>Person to Call (if other than patient)</Description>
      <Value className={!data?.providerGetPatientCase?.callbackName ? 'noneVal' : ''}>{data?.providerGetPatientCase?.callbackName || 'none'}</Value>
      <Description>Desired Callback Number</Description>
      <Value className={!data?.providerGetPatientCase?.callbackNumber ? 'noneVal' : ''}>{data?.providerGetPatientCase?.callbackNumber || 'none'}</Value>
      <Description>Subject</Description>
      <Value className={!data?.providerGetPatientCase?.subject ? 'noneVal' : ''}>{data?.providerGetPatientCase?.subject || 'none'}</Value>
      <Description>Priority</Description>
      <Value className={!data?.providerGetPatientCase?.priority ? 'noneVal' : ''}>{data?.providerGetPatientCase?.priority === '1' ? 'This task is urgent' : 'Not urgent'}</Value>
      <Description>Description</Description>
      <Value className={!data?.providerGetPatientCase?.internalNote ? 'noneVal' : ''}>{data?.providerGetPatientCase?.internalNote || 'none'}</Value>
    </Flex>)
}

export const PatientCaseWithParams: React.FC<{
  id: string,
  caseId: string,
  isInModal?: boolean,
  modalContainerStyle?: React.CSSProperties
}> = ({
  caseId,
  id,
  isInModal,
  modalContainerStyle={}
}) => {
    let navigate = useNavigate()
    const [modalize, setModalise] = useState(false)
    const [query, { data, loading, error }] = useLazyQuery<IGetPatientCaseOutput, IGetPatientCaseInput>(GET_PATIENT_CASE, { variables: { patientId: id!, patientCaseId: caseId! } })

    useEffect(() => {
      query()
    }, [])

    useEffect(() => {
      query()
    }, [caseId])

    if (!!loading || !data) {
      return <PleaseWait />
    }

    if (!!error) {
      return null
    }

    return (
      isInModal ? (
        <Box style={{ borderRadius: '16px', paddingBottom: '20px', marginBottom: '24px', paddingLeft:'32px', paddingRight:'32px', ...modalContainerStyle }} id="here">
          <Content data={data} />
        </Box>
      ) : (
        <>
          <Back onClick={() => navigate('../')}>{`< Back to cases`}</Back>
          <Card px={4} style={{ borderRadius: '16px', paddingBottom: '20px', margin: '12px', paddingTop: '12px', overflow: "hidden" }}>
            <Flex alignItems={'center'} justifyContent={'flex-end'} mb={2} style={{
              cursor: 'pointer'
            }} onClick={() => { setModalise(true) }}>
              <i className="fa-solid fa-up-right-and-down-left-from-center"></i>
              <Text ml={2} style={{ textDecoration: 'underline' }}>Expand</Text>
            </Flex>
            <Content data={data} />
          </Card>
          {!!modalize && (
            <Modal
              isOpen={true}
              onBackgroundClick={() => {
                setModalise(false);
              }}
              onEscapeKeydown={() => {
                setModalise(false);
              }}
            >
              <SpecialModalBackground
                onClick={() => {
                  setModalise(false);
                }}
              >
                <Box
                  style={{
                    margin: '0 auto',
                    alignSelf: 'center',
                    borderRadius: '20px',
                    position: 'relative',
                    background: 'linear-gradient(180deg, #BBD4ED 0%, #FDF5F2 100%)',
                    padding: '24px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                    maxWidth: '95vw',
                    minWidth: 'initial',
                    maxHeight: '95vh',
                    minHeight: '95vh',
                    overflowY: 'hidden',
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  <Wrapper flex={1} flexDirection={'column'} width={'800px'} style={{ overflowY: 'auto' }}>
                    <Content data={data} />
                  </Wrapper>
                </Box>
              </SpecialModalBackground>
            </Modal>
          )}
        </>
      )
    )
  }

const PatientCase = () => {

  let params = useParams<{
    id: string,
    caseId: string
  }>()
  return <PatientCaseWithParams id={params.id!} caseId={params.caseId!} />
}

export default PatientCase